import { db } from './db';
import type { AvatarState, HouseMode } from '../types/anna';
import type { Tables } from '../types/db';
import type { HouseModeConfig } from '../types/settings';
import type { Activity } from '../types/stage';

/**
 * The house rules every surface follows. One place decides what Anna may do in which mode,
 * driven by the owner's settings in Beheer → Anna → Huismodi.
 */

export function modeConfig(t: Tables, mode: HouseMode = t.meta.mode): HouseModeConfig | undefined {
  return t.settings.houseModes.find((m) => m.mode === mode);
}

/** Sounds follow the mode's sound setting. Real safety alarms always come through. */
export function canPlaySound(safety = false): boolean {
  if (safety) return true;
  return (modeConfig(db.getState())?.sound ?? 'aan') !== 'uit';
}

/** Spoken replies: full volume, softer in "zacht", whisper-level when sound is off for the mode. */
export function speechVolume(): number {
  const s = modeConfig(db.getState())?.sound ?? 'aan';
  return s === 'aan' ? 1 : 0.5;
}

/** Quiet modes: Anna dims and only reports what is urgent. */
export function isQuietMode(mode: HouseMode): boolean {
  return mode === 'Focus' || mode === 'Slapen';
}

/** One precedence for every screen: Noodstop > listening > thinking > speaking > a short reaction > rest. */
export function resolveAvatarState(input: {
  stopped: boolean;
  activity: Activity;
  transient: AvatarState | null;
  rest: AvatarState;
}): AvatarState {
  if (input.stopped) return 'gestopt';
  if (input.activity === 'listening') return 'luistert';
  if (input.activity === 'thinking') return 'denkt';
  if (input.activity === 'speaking') return 'spreekt';
  return input.transient ?? input.rest;
}
